// item-selector.component.ts
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { Logger } from 'common/utility/logger/logger';

export interface SelectableItem {
  id: any;
  name: string;
  [key: string]: any;
}

@Component({
  selector: 'app-item-selector',
  templateUrl: './item-selector.component.html',
  styleUrls: ['./item-selector.component.scss']
})
export class ItemSelectorComponent implements OnInit, OnChanges {
  @Input() items: SelectableItem[] = [];
  @Input() selectedItems: any[] = [];
  @Input() multiple = true;
  @Input() placeholder = 'Select items';
  @Input() label = 'Items';
  @Input() bindValue: 'id' | 'object' = 'id';

  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() selectedItemsChange = new EventEmitter<any[]>();

  filteredItems: SelectableItem[] = [];
  searchControl = new FormControl('');
  allSelected = false;

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.filteredItems = [...this.items];
    this.searchControl.valueChanges.subscribe(value => this.filterItems(value || ''));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items'] || changes['selectedItems']) {
      this.filteredItems = [...this.items];
      this.filterItems(this.searchControl.value || '');
      this.updateAllSelectedState();
    }
  }

  private filterItems(search: string) {
    const term = search.trim().toLowerCase();
    this.filteredItems = term
      ? this.items.filter(item => item.name.toLowerCase().includes(term))
      : [...this.items];
    this.updateAllSelectedState();
  }

  toggleAll(event: any) {
    const visibleValues = this.filteredItems.map(i => this.getValue(i));
    Logger.debug(`Event Checked: ${event.checked}`)

    const newSelection = event.checked
      ? Array.from(new Set([...this.selectedItems ?? [], ...visibleValues ?? []]))
      : this.selectedItems.filter(v => !visibleValues.includes(v));

    this.updateSelection(newSelection);
  }

  toggleItem(item: SelectableItem, event: MatCheckboxChange) {
    const value = this.getValue(item);
    this.selectedItems = !!this.selectedItems ? this.selectedItems : []
    const newSelection = event.checked
      ? this.multiple
        ? [...this.selectedItems, value]
        : [value]
      : this.selectedItems.filter(v => v !== value);

    this.updateSelection(newSelection);
  }

  removeItem(selectedValue: any) {
    const valueToRemove = this.bindValue === 'object' ? selectedValue : selectedValue;
    const newSelection = this.selectedItems.filter(v =>
      this.bindValue === 'object' ? v !== valueToRemove : v !== selectedValue
    );
    this.updateSelection(newSelection);
  }

  // CENTRAL METHOD — always creates new array + emits + updates state
  private updateSelection(newSelection: any[]) {
    this.selectedItems = [...newSelection] ?? [];  // ← NEW REFERENCE EVERY TIME
    this.selectionChange.emit(this.selectedItems);
    this.selectedItemsChange.emit(this.selectedItems);
    this.updateAllSelectedState();
    this.cdr.markForCheck();  // ← Forces chips to update immediately
  }

  isSelected(item: SelectableItem): boolean {
    if (!this.selectedItems) return false

    const value = this.getValue(item);
    return this.selectedItems.some(v =>
      this.bindValue === 'id' ? v === value : v === item
    );
  }

  private getValue(item: SelectableItem): any {
    return this.bindValue === 'id' ? item.id : item;
  }

  private updateAllSelectedState() {
    this.allSelected = this.filteredItems.length > 0 &&
      this.filteredItems.every(item => this.isSelected(item));
  }

  get someSelectedButNotAll(): boolean {
    if (this.filteredItems.length === 0) return false;
    const selectedCount = this.filteredItems.filter(i => this.isSelected(i)).length;
    return selectedCount > 0 && selectedCount < this.filteredItems.length;
  }

  getSelectedItemName(value: any): string {
    if (this.bindValue === 'object') return (value as SelectableItem)?.name ?? 'Unknown';
    return this.items.find(i => i.id === value)?.name ?? 'Unknown';
  }
}